import { useCallback, useEffect, useRef } from "react";
import type { PollingResult } from "@/payroll/pay-breakdown/payroll-statement-notifications/polling";
import type { PayrollStatementProjectionBuild } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import { getDisplayedProjectionBuild } from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import { useGetPayrollStatementNotifications } from "./notifications";
import { usePayrollStatementProjectionBuild } from "./projection-build";

export const usePayrollStatementNotifications = (
  businessId: string | null,
  periodStart: string | null,
) => {
  const fetchedProjectionGenerationRef = useRef<string | null>(null);
  useEffect(() => {
    fetchedProjectionGenerationRef.current = null;
  }, [businessId, periodStart]);

  const notificationsQuery = useGetPayrollStatementNotifications(
    businessId,
    periodStart,
  );
  const projectionQuery = usePayrollStatementProjectionBuild(
    businessId,
    periodStart,
    notificationsQuery.data?.projection_build,
  );
  const projectionBuild: PollingResult<PayrollStatementProjectionBuild> | null =
    projectionQuery.projectionBuild;
  const projectionReady =
    notificationsQuery.data?.projection_build?.status === "ready";
  const refetchNotifications = notificationsQuery.refetch;
  const hydrateReadyProjection = useCallback(
    async (generationKey: string | null) => {
      const result = await refetchNotifications();
      if (result.data?.projection_build?.status === "ready") {
        fetchedProjectionGenerationRef.current = generationKey;
      }
      return result;
    },
    [refetchNotifications],
  );

  useEffect(() => {
    const generationKey = projectionBuild?.generation_key;
    if (
      projectionBuild?.status !== "ready" ||
      projectionReady ||
      !generationKey ||
      fetchedProjectionGenerationRef.current === generationKey
    ) {
      return;
    }

    void hydrateReadyProjection(generationKey);
  }, [hydrateReadyProjection, projectionBuild, projectionReady]);

  const canRetryProjectionHydration =
    projectionBuild?.status === "ready" &&
    !projectionReady &&
    !!notificationsQuery.error;
  const refetchProjectionBuild = () => {
    if (projectionBuild?.status === "ready" && !projectionReady) {
      return hydrateReadyProjection(projectionBuild.generation_key);
    }

    return projectionQuery.refetch();
  };

  return {
    ...currentPayrollResult(notificationsQuery),
    projectionBuild: getDisplayedProjectionBuild({
      indexBuild: notificationsQuery.data?.projection_build,
      polledBuild: projectionBuild,
    }),
    isProjectionRefreshing:
      projectionQuery.isFetching || notificationsQuery.isFetching,
    canRetryProjectionHydration,
    refetchProjectionBuild,
  };
};
